import type { PermissionCode } from '@solar/shared';
import {
  BarChart3,
  Home,
  Newspaper,
  Package2,
  Boxes,
  Factory,
  Landmark,
  FileText,
  LayoutTemplate,
  Users,
  Settings,
  ScrollText,
  CalendarClock,
  HardHat,
  PencilRuler,
  Activity,
  IdCard,
  KeyRound,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  perms?: PermissionCode[];
  /** Module ứng dụng riêng (sub-app) trên cùng domain — điều hướng toàn trang, không qua router */
  external?: boolean;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

/** Menu điều hướng — chỉ hiển thị mục người dùng có quyền (backend vẫn kiểm tra lại). */
export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Trang chủ', icon: Home },
      { to: '/tin-noi-bo', label: 'Tin nội bộ', icon: Newspaper, perms: ['post.view', 'post.manage'] },
    ],
  },
  {
    title: 'Kinh doanh & báo giá',
    items: [
      {
        to: '/bao-gia',
        label: 'Báo giá',
        icon: FileText,
        perms: ['quote.create', 'quote.view_all', 'quote.view_team'],
      },
      { to: '/goi-san-pham', label: 'Gói sản phẩm', icon: Package2, perms: ['package.view', 'quote.create'] },
      { to: '/vat-tu', label: 'Danh mục vật tư', icon: Boxes, perms: ['product.view'] },
      {
        to: '/hang-san-xuat',
        label: 'Hãng sản xuất & tương thích',
        icon: Factory,
        perms: ['product.view', 'manufacturer.manage', 'compatibility.manage'],
      },
      {
        to: '/tai-chinh',
        label: 'Gói vay & tài chính',
        icon: Landmark,
        perms: ['financing.view', 'financing.manage'],
      },
      {
        to: '/thong-ke',
        label: 'Thống kê báo giá',
        icon: BarChart3,
        perms: ['quote.stats', 'quote.view_all'],
      },
      { to: '/mau-bao-gia', label: 'Mẫu báo giá', icon: LayoutTemplate, perms: ['template.manage'] },
    ],
  },
  {
    title: 'Kỹ thuật & thi công',
    items: [
      {
        to: '/lich-ky-thuat',
        label: 'Lịch kỹ thuật & khảo sát',
        icon: CalendarClock,
        perms: ['booking.use', 'booking.manage'],
      },
      {
        to: '/thi-cong/',
        label: 'Quản lý thi công',
        icon: HardHat,
        perms: ['construction.access'],
        external: true,
      },
      {
        // Quy trình thiết kế bắt đầu từ bố trí tấm pin, sau đó sang khối lượng vật tư (/thiet-ke/)
        to: '/thiet-ke/bo-tri/',
        label: 'Thiết kế & bố trí tấm pin',
        icon: PencilRuler,
        perms: ['design.use'],
        external: true,
      },
      {
        to: '/mo-phong/',
        label: 'Mô phỏng điện mặt trời',
        icon: Activity,
        perms: ['simulator.use'],
        external: true,
      },
      {
        to: '/thong-ke-thiet-ke',
        label: 'Thống kê thiết kế',
        icon: BarChart3,
        perms: ['design.stats'],
      },
    ],
  },
  {
    title: 'Nhân sự',
    items: [
      {
        to: '/nhan-su',
        label: 'Nhân sự kỹ thuật & kinh doanh',
        icon: IdCard,
        perms: ['staff.view', 'staff.manage', 'score.view', 'score.manage'],
      },
    ],
  },
  {
    title: 'Quản trị hệ thống',
    items: [
      {
        to: '/nhan-vien',
        label: 'Tài khoản & phân quyền',
        icon: Users,
        perms: ['user.manage', 'role.manage'],
      },
      { to: '/tich-hop', label: 'Tích hợp & khóa API', icon: KeyRound, perms: ['integration.manage'] },
      { to: '/cai-dat', label: 'Cài đặt', icon: Settings, perms: ['settings.manage'] },
      { to: '/nhat-ky', label: 'Nhật ký hoạt động', icon: ScrollText, perms: ['audit.view'] },
    ],
  },
];

/** Danh sách phẳng (giữ tương thích cho nơi khác dùng). */
export const NAV: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);
